import { useEffect, useRef, useState } from 'react';
import log from '../../../../common/utils/logger';

/**
 * useEditorSync
 *
 * Synchronizes the editor content with the server state (submissionText).
 * CRITICAL: This must only populate the editor on INITIAL load or explicit task switch.
 * It must NEVER overwrite user work with stale server data during re-renders or background fetches.
 *
 * Returns { status, retryCount } to allow the UI to show initialization progress/errors.
 */
export default function useEditorSync({
  editorRef, isTasksLoading, submissionText, taskId,
}) {
  const [status, setStatus] = useState('idle');
  const isInitialLoadRef = useRef(true);
  const loadedTaskIdRef = useRef(taskId);
  const [retryCount, setRetryCount] = useState(0);

  // Reset initial load state when task changes
  if (loadedTaskIdRef.current !== taskId) {
    isInitialLoadRef.current = true;
    loadedTaskIdRef.current = taskId;
    setRetryCount(0);
    setStatus('idle');
  }

  useEffect(() => {
    const maxRetries = 8; // Slightly more retries for safety
    let isMounted = true;
    let localRetryCount = 0;

    const syncInitialContent = async () => {
      if (!isMounted) return;

      try {
        if (isTasksLoading || !editorRef.current) return;

        // Only run on initial load for this task
        if (!isInitialLoadRef.current) {
          setStatus('synced');
          return;
        }

        setStatus('syncing');

        // CAPTURE: Take a local snapshot of the ref to prevent null dereference after await points.
        // Even if editorRef.current becomes null later, our local 'editor' variable remains stable.
        const editor = editorRef.current;

        // Check if methods exist.
        if (!editor || !editor.getHtml || !editor.setHtml) {
          throw new Error('Editor methods not yet injected into ref');
        }

        const currentHtml = await editor.getHtml();

        // RE-VERIFY: The component might have unmounted, the task might have changed,
        // or the editor might have been removed from the DOM during the await.
        if (!isMounted || !editorRef.current) return;

        const next = submissionText || '';

        // Safety check: Only write if editor is effectively empty
        const isEmpty = !currentHtml || currentHtml === '<p><br></p>';

        if (isEmpty && next !== '') {
          await editor.setHtml(next);
        }

        if (!isMounted) return;

        // Mark as loaded so we don't overwrite again
        isInitialLoadRef.current = false;
        setStatus('synced');
      } catch (err) {
        if (localRetryCount < maxRetries) {
          const delay = 50 * (2 ** localRetryCount);
          localRetryCount += 1;
          setRetryCount(localRetryCount);
          log.warn(`[FLARE-1C] Editor initialization attempt ${localRetryCount} failed, retrying in ${delay}ms...`, err.message);
          setTimeout(syncInitialContent, delay);
        } else {
          log.error('[FLARE-1C] Editor methods failed to initialize after max retries', err);
          setStatus('error');
        }
      }
    };

    syncInitialContent();

    return () => {
      isMounted = false;
    };
  }, [submissionText, isTasksLoading, editorRef, taskId]);

  return { status, retryCount };
}
